import { Municipality } from '../../../administration/type';
import { handleApiError, performLogout } from '../../../utils/errorHandler';
import apiClient from '../base/apiClient';

export const adminApi = {
  getList: async (resource: string, params: any) => {
    const { page, perPage } = params.pagination || { page: 0, perPage: 100 };
    const { filter } = params.filter || {};
    const response = await apiClient.get('/Administration/ManageQueryAdmins', {
      params: { filter: filter || '', page, count: perPage },
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const data = response.data.accounts || [];
    return {
      data: data.map((admin: any) => ({
        ...admin,
        id: admin.administratorId,
        profileImageUrl: admin.profileImageUrl !== "" ? `${apiClient.defaults.baseURL}${admin.profileImageUrl}` : "",
        profileThumbnailUrl: admin.profileThumbnailUrl !== "" ? `${apiClient.defaults.baseURL}${admin.profileThumbnailUrl}` : "",
      })),
      total: data.length,
    };
  },

  getAdminDetails: async (resource: string) => {
    const response = await apiClient.get('/Administration/Details');
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return {
      data: {
        ...response.data,
        profileImageUrl: `${apiClient.defaults.baseURL}${response.data.profileImageUrl}` || "",
        profileThumbnailUrl: `${apiClient.defaults.baseURL}${response.data.profileThumbnailUrl}` || "",
      },
    };
  },

  create: async (resource: string, params: any) => {
    const { data } = params;
    const response = await apiClient.post('/Administration/ManageCreate', {
      username: data.username,
      password: data.password,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { ...data, id: response.data.administratorId || 0 } };
  },

  update: async (resource: string, params: any) => {
    const { id, data } = params;
    const response = await apiClient.post('/Administration/ManageUpdate', {
      administratorId: id,
      email: data.email,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      phoneNumber: data.phoneNumber,
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { id, ...data } };
  },

  delete: async (resource: string, params: any) => {
    const { id } = params;
    const response = await apiClient.get('/Administration/ManageDelete', { params: { administratorId: id } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { id } };
  },

  getAdminCount: async (params: { filter?: string; count?: number } = {}) => {
    const { filter = '', count = 5 } = params;
    const response = await apiClient.get('/Administration/ManageQueryAdminsCount', { params: { filter, count } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { count: response.data.count || 0, pages: response.data.pages || 0 } };
  },

  customResetPassword: async (params: { id: number; currentPassword: string; newPassword: string }) => {
    const { id, currentPassword, newPassword } = params;
    const response = await apiClient.post('/Administration/ManageResetPassword', {
      AdministratorId: id,
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return response.data;
  },

  manageUploadProfileImage: async (params: { administratorId: number; file: File }) => {
    const { administratorId, file } = params;
    const formData = new FormData();
    formData.append('profileImage', file);
    const response = await apiClient.post(`/Administration/ManageUploadProfileImage?administratorId=${administratorId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: { success: true } };
  },

  assignRoles: async (params: { id: number; roleIds: number[] }) => {
    const { id, roleIds } = params;
    const response = await apiClient.post('/Administration/ManageAssignRoles', { administratorId: id, roleIds });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: {} };
  },

  getAssignedRoles: async (params: { id: number }) => {
    const { id: administratorId } = params;
    const response = await apiClient.get('/Administration/ManageAssignedRoles', { params: { administratorId } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const roles = response.data.roles || [];
    return {
      data: roles.map((role: any) => ({
        id: role.roleId,
        name: role.roleName,
        rights: [{ title: role.roleName, description: role.roleDescription || '' }],
      })),
    };
  },

  getAllProvinces: async (resource: string) => {
    const response = await apiClient.get(`/Administration/Address/ListAllProvinces`, { withCredentials: true });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const provinces: { id: number; name: string }[] = response.data.provinces.map((province: any) => ({
      id: province.provinceId,
      name: province.name,
    }));
    return { data: provinces };
  },

  lookupCities: async (resource: string, params: { name: string; page?: number; count?: number } = { name: '', page: 0, count: 50 }) => {
    const { name, page, count  } = params;
    const response = await apiClient.get(`/Administration/Address/LookupCities`, {
      params: { name, count, page },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const municipalities: Municipality[] = response.data.cities.map((city: any) => ({
      id: city.cityId,
      name: city.name,
      province: city.province,
    }));
    return { data: municipalities, total: response.data.total || 0  };
  },

  getCitiesByProvince: async (resource: string, params: { provinceId: number; page?: number; count?: number } = { provinceId: 0, page: 0, count: 100 }) => {
    const { provinceId, page, count } = params;

    const response = await apiClient.get(`/Administration/Address/ListAllCitiesByProvince`, {
      params: { provinceId, count, page },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const municipalities: Municipality[] = response.data.cities.map((city: any) => ({
      id: city.cityId,
      name: city.name,
      province: city.province.name,
    }));
    return { data: municipalities, total: municipalities.length };
  },

  getAllRights: async (resource: string) => {
    const response = await apiClient.get(`/Administration/Right/ListAll`, { withCredentials: true });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const rights = response.data.rights || [];
    return {
      data: rights.map((right: any) => ({
        id: right.rightId.toString(),
        name: right.name,
        description: right.description || '',
      })),
    };
  },

  getAssignedRights: async (resource: string, params: { id: string }) => {
    const { id: roleId } = params;
    const response = await apiClient.get(`/Administration/RoleRight/AssignedRights`, {
      params: { roleId },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const rights = response.data.rights || [];
    return {
      data: rights.map((right: any) => ({
        id: right.rightId.toString(),
        name: right.rightName,
        description: right.rightDescription || '',
      })),
    };
  },

  assignRights: async (resource: string, params: { id: string; rightIds: string[] }) => {
    const { id: roleId, rightIds } = params;
    const response = await apiClient.post(
      `/Administration/RoleRight/Assign`,
      { roleId, rightIds },
      { withCredentials: true }
    );

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return { data: {} };
  },

  updateRole: async (resource: string, params: { id: string; name: string; description: string }) => {
    const { id, name, description } = params;
    const response = await apiClient.put(
      `/Administration/Role/Update`,
      { roleId: id, name, description },
      { withCredentials: true }
    );

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return {
      data: { id, name, description, rights: [{ title: name, description: description || '' }] },
    };
  },

  createRole: async (resource: string, param: { name: string; description: string }) => {
    const { name, description } = param;
    const response = await apiClient.post(`/Administration/Role/Create`, { name, description }, { withCredentials: true });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    if (!response.data) {
      throw new Error('API did not return a valid role ID');
    }

    return {
      data: {
        id: response.data.roleId,
        name,
        description,
        rights: [{ title: name, description: description || '' }],
      },
    };
  },

  deleteRole: async (resource: string, params: { id: string }) => {
    const { id } = params;
    const response = await apiClient.delete(`/Administration/Role/Delete`, {
      params: { roleId: id },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return { data: { id } };
  },

  getAllRoles: async (resource: string) => {
    const response = await apiClient.get(`/Administration/Role/ListAll`, { withCredentials: true });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const roles = response.data.roles || [];
    return {
      data: roles.map((role: any) => ({
        id: role.roleId.toString(),
        name: role.name,
        description: role.description,
        rights: [{ title: role.name, description: role.description || '' }],
      })),
    };
  },

  getAllMunicipalities: async (resource: string, params: { page?: number; count?: number } = { page: 0, count: 100 }) => {
    const { page , count } = params;
    const response = await apiClient.get(`/Administration/Address/ListAllCities`, {
      params: { count, page },
      withCredentials: true,
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    const municipalities: Municipality[] = response.data.cities.map((city: any) => ({
      id: city.cityId,
      name: city.name,
      province: city.province,
    }));
    return { data: municipalities, total: response.data.total };
  },

  uploadProfileImage: async (resource: string, params: { file: File }) => {
    const { file } = params;
    const formData = new FormData();
    formData.append('profileImage', file);

    const response = await apiClient.post(`/Administration/UploadProfileImage`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (response.data.errorMessage) {
      throw new Error(response.data.errorMessage);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }

    return { data: { success: true } };
  },

  getAssignedMunicipalities: async (params: { id: number }) => {
    const { id: administratorId } = params;
    const response = await apiClient.get('/Administration/ManageAssignedCities', { params: { administratorId } });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    const municipalities = response.data.cities.map((city: any) => ({
      id: city.cityId,
      name: city.city.name,
      province: city.city.province,
    }));
    return { data: municipalities };
  },

  assignMunicipalities: async (params: { id: number; municipalityIds: number[] }) => {
    const { id: administratorId, municipalityIds } = params;
    const response = await apiClient.post('/Administration/ManageAssignCities', { administratorId, cityIds: municipalityIds });
    if (response.data.errorMessage) throw new Error(response.data.errorMessage);
    if (response.data.error) throw new Error(response.data.error);
    return { data: {} };
  },

  checkError: (error: any) => {
    const apiError = handleApiError(error);
    if (apiError.shouldLogout || error.response?.status === 401) {
      performLogout();
      throw new Error('Unauthorized');
    }
    return Promise.resolve();
  },

  getPermissions: () => Promise.resolve(),
};